/**
 * Client PowerPoint utilisant @devana/ws-tools
 * Version simplifiée pour l'API PowerPoint actuelle
 */

import { DevanaWSClient } from "@devana/ws-tools";

// Créer le client Devana pour PowerPoint
export function createPowerPointClient(
  apiUrl: string,
  options?: {
    documentName?: string;
    bearerToken?: string;
    callbacks?: {
      onConnected?: (sessionId: string) => void;
      onReconnected?: (sessionId: string) => void;
      onDisconnected?: () => void;
      onError?: (error: Error) => void;
      onSessionsChanged?: () => void;
    };
  }
) {
  // Essayer de récupérer le nom de la présentation depuis PowerPoint
  let docName = options?.documentName;

  if (!docName && typeof Office !== "undefined" && Office.context?.document) {
    // Tenter de récupérer le nom du fichier PowerPoint
    try {
      docName =
        (Office.context.document as any).url
          ?.split("/")
          .pop()
          ?.replace(".pptx", "") || "Présentation PowerPoint";
    } catch (e) {
      docName = "Présentation PowerPoint";
    }
  }

  const client = new DevanaWSClient({
    apiUrl,
    clientType: "powerpoint",
    documentName: docName,
    bearerToken: options?.bearerToken,
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 2000,
    metadata: {
      host: (Office?.context?.host as any) || "PowerPoint",
      platform: (Office?.context?.platform as any) || "unknown",
    },
    onConnected: (sessionId) => {
      console.log("[PowerPointClient] ✅ Connected with session:", sessionId);
      console.log("[PowerPointClient] Presentation:", docName);

      // Ré-enregistrer les tools à chaque connexion
      console.log("[PowerPointClient] 📝 Re-registering tools after connection...");
      registerPowerPointTools(client);

      options?.callbacks?.onConnected?.(sessionId);
    },
    onReconnected: (sessionId) => {
      console.log("[PowerPointClient] 🔄 Reconnected with session:", sessionId);

      // Ré-enregistrer les tools après reconnexion
      console.log("[PowerPointClient] 📝 Re-registering tools after reconnection...");
      registerPowerPointTools(client);

      options?.callbacks?.onReconnected?.(sessionId);
    },
    onDisconnected: () => {
      console.log("[PowerPointClient] ⚠️ Disconnected");
      options?.callbacks?.onDisconnected?.();
    },
    onError: (error) => {
      console.error("[PowerPointClient] ❌ Error:", error);
      options?.callbacks?.onError?.(error);
    },
    onSessionsChanged: () => {
      console.log("[PowerPointClient] 🔄 Sessions changed notification");
      options?.callbacks?.onSessionsChanged?.();
    },
  });

  // Enregistrer tous les tools PowerPoint
  registerPowerPointTools(client);

  return client;
}

/**
 * Enregistre tous les tools PowerPoint avec leurs handlers
 * Version simplifiée pour l'API actuelle
 */
function registerPowerPointTools(client: DevanaWSClient) {
  // === PRESENTATION INFO TOOLS ===

  client.registerTool("get_presentation_info", {
    description: "Get presentation information including slide count and title",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return new Promise((resolve, reject) => {
        Office.context.document.getFilePropertiesAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const url = result.value.url || "Untitled";
            const fileName = url.split("/").pop() || "Presentation";

            // Obtenir le nombre de slides via PowerPoint API
            PowerPoint.run(async (context) => {
              try {
                const presentation = context.presentation;
                presentation.load("title");
                const slides = presentation.slides;
                slides.load("items");

                await context.sync();

                resolve({
                  fileName,
                  title: presentation.title || fileName.replace(".pptx", ""),
                  slideCount: slides.items.length,
                  url: url,
                });
              } catch (error) {
                resolve({
                  fileName,
                  title: fileName.replace(".pptx", ""),
                  slideCount: 0,
                  url: url,
                  error: "Could not load presentation details"
                });
              }
            });
          } else {
            reject(new Error("Failed to get presentation info"));
          }
        });
      });
    },
  });

  // === SLIDE NAVIGATION TOOLS ===

  client.registerTool("get_current_slide", {
    description: "Get the currently selected slide number and content",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        const selection = context.presentation.getSelectedSlides();
        selection.load("items");
        await context.sync();

        if (selection.items.length > 0) {
          const slide = selection.items[0];
          slide.load(["id"]);

          // Charger les shapes de la slide
          const shapes = slide.shapes;
          shapes.load("items");
          await context.sync();

          const content: any[] = [];
          for (const shape of shapes.items) {
            shape.load(["type", "id"]);
            await context.sync();

            if (shape.type === PowerPoint.ShapeType.textBox) {
              const textFrame = shape.textFrame;
              textFrame.load("textRange");
              await context.sync();

              if (textFrame.textRange) {
                textFrame.textRange.load("text");
                await context.sync();
                content.push({
                  type: "text",
                  text: textFrame.textRange.text,
                });
              }
            }
          }

          // Obtenir l'index de la slide
          const slides = context.presentation.slides;
          slides.load("items");
          await context.sync();

          let slideIndex = -1;
          for (let i = 0; i < slides.items.length; i++) {
            if (slides.items[i].id === slide.id) {
              slideIndex = i + 1; // 1-indexed for user
              break;
            }
          }

          return {
            slideNumber: slideIndex,
            slideId: slide.id,
            content: content,
          };
        }

        return {
          slideNumber: 0,
          content: [],
          message: "No slide selected",
        };
      });
    },
  });

  client.registerTool("go_to_slide", {
    description: "Navigate to a specific slide by number",
    schema: {
      type: "object",
      properties: {
        slideNumber: {
          type: "number",
          description: "Slide number to navigate to (1-indexed)",
        },
      },
      required: ["slideNumber"],
    },
    handler: async (args) => {
      const { slideNumber } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        if (slideNumber < 1 || slideNumber > slides.items.length) {
          throw new Error(`Slide number ${slideNumber} is out of range (1-${slides.items.length})`);
        }

        const targetSlide = slides.items[slideNumber - 1];
        // Sélectionner la slide
        context.presentation.setSelectedSlides([targetSlide.id]);
        await context.sync();

        return {
          success: true,
          currentSlide: slideNumber,
          totalSlides: slides.items.length,
        };
      });
    },
  });

  // === SLIDE CONTENT TOOLS ===

  client.registerTool("get_all_slides_content", {
    description: "Get content from all slides in the presentation",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        const allSlidesContent = [];

        for (let i = 0; i < slides.items.length; i++) {
          const slide = slides.items[i];
          slide.load(["id"]);

          // Charger les shapes
          const shapes = slide.shapes;
          shapes.load("items");

          await context.sync();

          const slideContent: any = {
            slideNumber: i + 1,
            slideId: slide.id,
            content: [],
          };

          // Extraire le contenu des shapes
          for (const shape of shapes.items) {
            shape.load(["type", "id", "name"]);
            await context.sync();

            if (shape.type === PowerPoint.ShapeType.textBox) {
              const textFrame = shape.textFrame;
              textFrame.load("textRange");
              await context.sync();

              if (textFrame.textRange) {
                textFrame.textRange.load("text");
                await context.sync();

                if (textFrame.textRange.text.trim()) {
                  slideContent.content.push({
                    type: "text",
                    shapeName: shape.name,
                    text: textFrame.textRange.text,
                  });
                }
              }
            }
          }

          allSlidesContent.push(slideContent);
        }

        return {
          totalSlides: slides.items.length,
          slides: allSlidesContent,
        };
      });
    },
  });

  client.registerTool("get_slide_content", {
    description: "Get content from a specific slide",
    schema: {
      type: "object",
      properties: {
        slideNumber: {
          type: "number",
          description: "Slide number (1-indexed)",
        },
      },
      required: ["slideNumber"],
    },
    handler: async (args) => {
      const { slideNumber } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        if (slideNumber < 1 || slideNumber > slides.items.length) {
          throw new Error(`Slide number ${slideNumber} is out of range (1-${slides.items.length})`);
        }

        const slide = slides.items[slideNumber - 1];
        slide.load(["id"]);

        const shapes = slide.shapes;
        shapes.load("items");

        await context.sync();

        const content = [];

        for (const shape of shapes.items) {
          shape.load(["type", "id", "name", "left", "top", "width", "height"]);
          await context.sync();

          const shapeInfo: any = {
            type: shape.type,
            name: shape.name,
            position: {
              left: shape.left,
              top: shape.top,
              width: shape.width,
              height: shape.height,
            },
          };

          if (shape.type === PowerPoint.ShapeType.textBox) {
            const textFrame = shape.textFrame;
            textFrame.load("textRange");
            await context.sync();

            if (textFrame.textRange) {
              textFrame.textRange.load(["text", "font"]);
              await context.sync();

              shapeInfo.text = textFrame.textRange.text;
              shapeInfo.font = {
                name: textFrame.textRange.font.name,
                size: textFrame.textRange.font.size,
                bold: textFrame.textRange.font.bold,
                italic: textFrame.textRange.font.italic,
                underline: textFrame.textRange.font.underline,
                color: textFrame.textRange.font.color,
              };
            }
          }

          content.push(shapeInfo);
        }

        return {
          slideNumber: slideNumber,
          slideId: slide.id,
          content: content,
        };
      });
    },
  });

  // === SLIDE MODIFICATION TOOLS ===

  client.registerTool("add_slide", {
    description: "Add a new slide to the presentation",
    schema: {
      type: "object",
      properties: {
        position: {
          type: "number",
          description: "Position where to insert the slide (1-indexed). If not specified, adds at the end.",
        },
      },
    },
    handler: async (args) => {
      const { position } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;

        let options: any = {};

        if (position !== undefined) {
          slides.load("items");
          await context.sync();

          if (position < 1 || position > slides.items.length + 1) {
            throw new Error(`Position ${position} is out of range (1-${slides.items.length + 1})`);
          }

          // PowerPoint API uses 0-indexed for slide index
          options.index = position - 1;
        }

        // Add the slide
        slides.add(options);
        await context.sync();

        // Get the new slide
        slides.load("items");
        await context.sync();

        // La nouvelle slide sera à l'index spécifié ou à la fin
        const newSlideIndex = options.index !== undefined ? options.index : slides.items.length - 1;
        const newSlide = slides.items[newSlideIndex];
        const newSlideNumber = newSlideIndex + 1;

        return {
          success: true,
          slideId: newSlide.id,
          slideNumber: newSlideNumber,
          totalSlides: slides.items.length,
        };
      });
    },
  });

  client.registerTool("delete_slide", {
    description: "Delete a slide from the presentation",
    schema: {
      type: "object",
      properties: {
        slideNumber: {
          type: "number",
          description: "Slide number to delete (1-indexed)",
        },
      },
      required: ["slideNumber"],
    },
    handler: async (args) => {
      const { slideNumber } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        if (slideNumber < 1 || slideNumber > slides.items.length) {
          throw new Error(`Slide number ${slideNumber} is out of range (1-${slides.items.length})`);
        }

        if (slides.items.length === 1) {
          throw new Error("Cannot delete the last slide in the presentation");
        }

        const slideToDelete = slides.items[slideNumber - 1];
        slideToDelete.delete();
        await context.sync();

        return {
          success: true,
          deletedSlide: slideNumber,
          remainingSlides: slides.items.length - 1,
        };
      });
    },
  });

  client.registerTool("add_text_to_slide", {
    description: "Add or update text on a slide",
    schema: {
      type: "object",
      properties: {
        slideNumber: {
          type: "number",
          description: "Slide number (1-indexed)",
        },
        text: {
          type: "string",
          description: "Text to add",
        },
        shapeType: {
          type: "string",
          description: "Type of text shape to add or update",
          enum: ["title", "body", "custom"],
        },
        shapeName: {
          type: "string",
          description: "Name of the shape (for custom shapes)",
        },
      },
      required: ["slideNumber", "text"],
    },
    handler: async (args) => {
      const { slideNumber, text, shapeType = "body", shapeName } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        if (slideNumber < 1 || slideNumber > slides.items.length) {
          throw new Error(`Slide number ${slideNumber} is out of range (1-${slides.items.length})`);
        }

        const slide = slides.items[slideNumber - 1];
        const shapes = slide.shapes;
        shapes.load("items");
        await context.sync();

        let targetShape = null;

        if (shapeType === "title") {
          // Chercher le placeholder de titre
          for (const shape of shapes.items) {
            shape.load(["type", "name"]);
            await context.sync();

            if (shape.name && shape.name.toLowerCase().includes("title")) {
              targetShape = shape;
              break;
            }
          }
        } else if (shapeType === "body") {
          // Chercher le placeholder de contenu
          for (const shape of shapes.items) {
            shape.load(["type", "name"]);
            await context.sync();

            if (shape.name && (shape.name.toLowerCase().includes("content") ||
                               shape.name.toLowerCase().includes("text"))) {
              targetShape = shape;
              break;
            }
          }
        } else if (shapeType === "custom" && shapeName) {
          // Chercher par nom spécifique
          for (const shape of shapes.items) {
            shape.load(["name"]);
            await context.sync();

            if (shape.name === shapeName) {
              targetShape = shape;
              break;
            }
          }
        }

        if (targetShape) {
          // Mettre à jour le texte existant
          targetShape.load(["type", "textFrame"]);
          await context.sync();

          if (targetShape.type === PowerPoint.ShapeType.textBox) {
            const textFrame = targetShape.textFrame;
            textFrame.load("textRange");
            await context.sync();

            if (textFrame.textRange) {
              textFrame.textRange.text = text;
              await context.sync();

              return {
                success: true,
                action: "updated",
                slideNumber: slideNumber,
                shapeName: targetShape.name,
              };
            }
          }
        }

        // Si aucune shape trouvée, créer une nouvelle text box
        const newShapeOptions: any = {
          textContent: text,
          left: 100,
          top: 100,
          width: 400,
          height: 200,
        };

        const newShape = shapes.addTextBox(text, newShapeOptions);
        newShape.name = shapeName || `TextBox_${Date.now()}`;
        await context.sync();

        return {
          success: true,
          action: "created",
          slideNumber: slideNumber,
          shapeName: newShape.name,
        };
      });
    },
  });

  // === SEARCH TOOLS ===

  client.registerTool("search_in_presentation", {
    description: "Search for text in the presentation",
    schema: {
      type: "object",
      properties: {
        searchText: {
          type: "string",
          description: "Text to search for",
        },
        caseSensitive: {
          type: "boolean",
          description: "Case sensitive search",
        },
        wholeWords: {
          type: "boolean",
          description: "Match whole words only",
        },
      },
      required: ["searchText"],
    },
    handler: async (args) => {
      const { searchText, caseSensitive = false, wholeWords = false } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        const results = [];
        const searchRegex = new RegExp(
          wholeWords ? `\\b${searchText}\\b` : searchText,
          caseSensitive ? "g" : "gi"
        );

        for (let i = 0; i < slides.items.length; i++) {
          const slide = slides.items[i];
          const shapes = slide.shapes;
          shapes.load("items");
          await context.sync();

          const slideResults: any = {
            slideNumber: i + 1,
            matches: [],
          };

          for (const shape of shapes.items) {
            shape.load(["type", "name", "textFrame"]);
            await context.sync();

            if (shape.type === PowerPoint.ShapeType.textBox) {
              const textFrame = shape.textFrame;
              textFrame.load("textRange");
              await context.sync();

              if (textFrame.textRange) {
                textFrame.textRange.load("text");
                await context.sync();

                const text = textFrame.textRange.text;
                const matches = text.match(searchRegex);

                if (matches && matches.length > 0) {
                  slideResults.matches.push({
                    shapeName: shape.name,
                    text: text,
                    matchCount: matches.length,
                    matches: matches,
                  });
                }
              }
            }
          }

          if (slideResults.matches.length > 0) {
            results.push(slideResults);
          }
        }

        return {
          searchText: searchText,
          totalMatches: results.reduce((sum, slide) =>
            sum + slide.matches.reduce((s: number, m: any) => s + m.matchCount, 0), 0),
          slidesWithMatches: results.length,
          results: results,
        };
      });
    },
  });

  // === STATISTICS AND ANALYSIS TOOLS ===

  client.registerTool("analyze_presentation_statistics", {
    description: "Get detailed statistics about the presentation",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        let totalWords = 0;
        let totalTextBoxes = 0;
        let totalShapes = 0;
        const slidesStats = [];

        for (let i = 0; i < slides.items.length; i++) {
          const slide = slides.items[i];
          const shapes = slide.shapes;
          shapes.load("items");
          await context.sync();

          let slideWords = 0;
          let slideTextBoxes = 0;

          for (const shape of shapes.items) {
            shape.load(["type", "textFrame"]);
            await context.sync();

            totalShapes++;

            if (shape.type === PowerPoint.ShapeType.textBox) {
              slideTextBoxes++;
              totalTextBoxes++;

              const textFrame = shape.textFrame;
              textFrame.load("textRange");
              await context.sync();

              if (textFrame.textRange) {
                textFrame.textRange.load("text");
                await context.sync();

                const text = textFrame.textRange.text;
                const words = text.trim().split(/\s+/).filter(w => w.length > 0);
                slideWords += words.length;
                totalWords += words.length;
              }
            }
          }

          slidesStats.push({
            slideNumber: i + 1,
            wordCount: slideWords,
            textBoxCount: slideTextBoxes,
            shapeCount: shapes.items.length,
          });
        }

        // Calculer le temps de lecture estimé (150 mots/minute)
        const estimatedReadingTime = Math.ceil(totalWords / 150);

        return {
          totalSlides: slides.items.length,
          totalWords: totalWords,
          totalTextBoxes: totalTextBoxes,
          totalShapes: totalShapes,
          estimatedReadingTimeMinutes: estimatedReadingTime,
          averageWordsPerSlide: Math.round(totalWords / slides.items.length),
          slidesDetails: slidesStats,
        };
      });
    },
  });

  client.registerTool("get_presentation_outline", {
    description: "Get the presentation outline based on slide titles",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        const outline = [];

        for (let i = 0; i < slides.items.length; i++) {
          const slide = slides.items[i];
          const shapes = slide.shapes;
          shapes.load("items");
          await context.sync();

          let slideTitle = "";
          let slideSubtitles = [];

          for (const shape of shapes.items) {
            shape.load(["type", "name", "textFrame"]);
            await context.sync();

            if (shape.type === PowerPoint.ShapeType.textBox) {
              const textFrame = shape.textFrame;
              textFrame.load("textRange");
              await context.sync();

              if (textFrame.textRange) {
                textFrame.textRange.load("text");
                await context.sync();

                const text = textFrame.textRange.text.trim();

                if (text && shape.name) {
                  if (shape.name.toLowerCase().includes("title") && !slideTitle) {
                    slideTitle = text;
                  } else if (shape.name.toLowerCase().includes("subtitle")) {
                    slideSubtitles.push(text);
                  }
                }
              }
            }
          }

          outline.push({
            slideNumber: i + 1,
            title: slideTitle || `Slide ${i + 1}`,
            subtitles: slideSubtitles,
          });
        }

        return {
          presentationOutline: outline,
          totalSlides: slides.items.length,
        };
      });
    },
  });

  // === BATCH OPERATIONS TOOLS ===

  client.registerTool("duplicate_slide", {
    description: "Duplicate an existing slide",
    schema: {
      type: "object",
      properties: {
        slideNumber: {
          type: "number",
          description: "Slide number to duplicate (1-indexed)",
        },
        insertAfter: {
          type: "boolean",
          description: "Insert the duplicate after the original (true) or at the end (false)",
        },
      },
      required: ["slideNumber"],
    },
    handler: async (args) => {
      const { slideNumber, insertAfter = true } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        if (slideNumber < 1 || slideNumber > slides.items.length) {
          throw new Error(`Slide number ${slideNumber} is out of range (1-${slides.items.length})`);
        }

        const originalSlide = slides.items[slideNumber - 1];
        const shapes = originalSlide.shapes;
        shapes.load("items");
        await context.sync();

        // Créer une nouvelle slide
        const newSlideOptions: any = {};
        if (insertAfter) {
          newSlideOptions.index = slideNumber;
        }

        slides.add(newSlideOptions);
        await context.sync();

        // Récupérer la nouvelle slide
        slides.load("items");
        await context.sync();

        const newSlideIndex = insertAfter ? slideNumber : slides.items.length - 1;
        const newSlide = slides.items[newSlideIndex];
        const newShapes = newSlide.shapes;

        // Copier le contenu
        for (const shape of shapes.items) {
          shape.load(["type", "left", "top", "width", "height", "name", "textFrame"]);
          await context.sync();

          if (shape.type === PowerPoint.ShapeType.textBox) {
            const textFrame = shape.textFrame;
            textFrame.load("textRange");
            await context.sync();

            if (textFrame.textRange) {
              textFrame.textRange.load("text");
              await context.sync();

              const newShape = newShapes.addTextBox(textFrame.textRange.text, {
                left: shape.left,
                top: shape.top,
                width: shape.width,
                height: shape.height,
              });
              newShape.name = shape.name;
              await context.sync();
            }
          }
        }

        return {
          success: true,
          originalSlide: slideNumber,
          duplicatedSlide: newSlideIndex + 1,
          totalSlides: slides.items.length,
        };
      });
    },
  });

  client.registerTool("reorder_slides", {
    description: "Move a slide to a different position",
    schema: {
      type: "object",
      properties: {
        fromPosition: {
          type: "number",
          description: "Current slide position (1-indexed)",
        },
        toPosition: {
          type: "number",
          description: "Target position (1-indexed)",
        },
      },
      required: ["fromPosition", "toPosition"],
    },
    handler: async (args) => {
      const { fromPosition, toPosition } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        if (fromPosition < 1 || fromPosition > slides.items.length) {
          throw new Error(`From position ${fromPosition} is out of range`);
        }
        if (toPosition < 1 || toPosition > slides.items.length) {
          throw new Error(`To position ${toPosition} is out of range`);
        }

        // Note: L'API PowerPoint actuelle ne supporte pas directement le réordonnancement
        // On doit dupliquer et supprimer
        return {
          success: false,
          message: "Slide reordering is not directly supported in current API. Use duplicate and delete instead.",
          suggestion: "Duplicate the slide to the desired position, then delete the original.",
        };
      });
    },
  });

  client.registerTool("replace_text_globally", {
    description: "Replace text throughout the entire presentation",
    schema: {
      type: "object",
      properties: {
        findText: {
          type: "string",
          description: "Text to find",
        },
        replaceText: {
          type: "string",
          description: "Text to replace with",
        },
        caseSensitive: {
          type: "boolean",
          description: "Case sensitive replacement",
        },
      },
      required: ["findText", "replaceText"],
    },
    handler: async (args) => {
      const { findText, replaceText, caseSensitive = false } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        let totalReplacements = 0;
        const replacementDetails = [];

        for (let i = 0; i < slides.items.length; i++) {
          const slide = slides.items[i];
          const shapes = slide.shapes;
          shapes.load("items");
          await context.sync();

          let slideReplacements = 0;

          for (const shape of shapes.items) {
            shape.load(["type", "name", "textFrame"]);
            await context.sync();

            if (shape.type === PowerPoint.ShapeType.textBox) {
              const textFrame = shape.textFrame;
              textFrame.load("textRange");
              await context.sync();

              if (textFrame.textRange) {
                textFrame.textRange.load("text");
                await context.sync();

                const originalText = textFrame.textRange.text;
                const flags = caseSensitive ? "g" : "gi";
                const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), flags);

                const newText = originalText.replace(regex, replaceText);

                if (originalText !== newText) {
                  textFrame.textRange.text = newText;
                  await context.sync();

                  const matches = originalText.match(regex);
                  const count = matches ? matches.length : 0;
                  slideReplacements += count;
                  totalReplacements += count;
                }
              }
            }
          }

          if (slideReplacements > 0) {
            replacementDetails.push({
              slideNumber: i + 1,
              replacements: slideReplacements,
            });
          }
        }

        return {
          success: true,
          findText: findText,
          replaceText: replaceText,
          totalReplacements: totalReplacements,
          slidesAffected: replacementDetails.length,
          details: replacementDetails,
        };
      });
    },
  });

  client.registerTool("create_summary_slide", {
    description: "Create a summary slide with key points from the presentation",
    schema: {
      type: "object",
      properties: {
        position: {
          type: "number",
          description: "Position to insert the summary slide (1-indexed)",
        },
        maxPointsPerSlide: {
          type: "number",
          description: "Maximum number of points to extract per slide",
        },
      },
    },
    handler: async (args) => {
      const { position, maxPointsPerSlide = 2 } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        // Collecter les points clés
        const keyPoints = [];

        for (let i = 0; i < slides.items.length; i++) {
          const slide = slides.items[i];
          const shapes = slide.shapes;
          shapes.load("items");
          await context.sync();

          const slidePoints = [];

          for (const shape of shapes.items) {
            shape.load(["type", "name", "textFrame"]);
            await context.sync();

            if (shape.type === PowerPoint.ShapeType.textBox) {
              const textFrame = shape.textFrame;
              textFrame.load("textRange");
              await context.sync();

              if (textFrame.textRange) {
                textFrame.textRange.load("text");
                await context.sync();

                const text = textFrame.textRange.text.trim();

                // Extraire les points principaux (titres ou premières lignes)
                if (text && shape.name) {
                  if (shape.name.toLowerCase().includes("title")) {
                    slidePoints.unshift(text); // Priorité aux titres
                  } else if (slidePoints.length < maxPointsPerSlide) {
                    const firstLine = text.split('\n')[0].trim();
                    if (firstLine.length > 10 && firstLine.length < 100) {
                      slidePoints.push(firstLine);
                    }
                  }
                }
              }
            }
          }

          if (slidePoints.length > 0) {
            keyPoints.push({
              slideNumber: i + 1,
              points: slidePoints.slice(0, maxPointsPerSlide),
            });
          }
        }

        // Créer la slide de résumé
        const summaryOptions: any = {};
        if (position !== undefined && position > 0 && position <= slides.items.length + 1) {
          summaryOptions.index = position - 1;
        }
        slides.add(summaryOptions);
        await context.sync();

        // Récupérer la nouvelle slide
        slides.load("items");
        await context.sync();

        const summaryPosition = (position !== undefined && position > 0 && position <= slides.items.length)
          ? position - 1
          : slides.items.length - 1;
        const summarySlide = slides.items[summaryPosition];
        const summaryShapes = summarySlide.shapes;

        // Ajouter le titre
        const titleShape = summaryShapes.addTextBox("Résumé de la présentation", {
          left: 50,
          top: 30,
          width: 600,
          height: 60,
        });
        titleShape.name = "Title";
        await context.sync();

        // Ajouter les points clés
        let contentText = "";
        keyPoints.forEach((slide) => {
          contentText += `Slide ${slide.slideNumber}:\n`;
          slide.points.forEach((point) => {
            contentText += `• ${point}\n`;
          });
          contentText += "\n";
        });

        const contentShape = summaryShapes.addTextBox(contentText, {
          left: 50,
          top: 100,
          width: 600,
          height: 400,
        });
        contentShape.name = "Content";
        await context.sync();

        return {
          success: true,
          summarySlideNumber: summaryPosition + 1,
          keyPointsExtracted: keyPoints.length,
          totalSlides: slides.items.length,
        };
      });
    },
  });

  client.registerTool("export_all_text", {
    description: "Export all text from the presentation in a structured format",
    schema: {
      type: "object",
      properties: {
        includeFormatting: {
          type: "boolean",
          description: "Include text formatting information",
        },
      },
    },
    handler: async (args) => {
      const { includeFormatting = false } = args;

      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        const exportedData = [];

        for (let i = 0; i < slides.items.length; i++) {
          const slide = slides.items[i];
          const shapes = slide.shapes;
          shapes.load("items");
          await context.sync();

          const slideData: any = {
            slideNumber: i + 1,
            textContent: [],
          };

          for (const shape of shapes.items) {
            shape.load(["type", "name", "left", "top", "width", "height", "textFrame"]);
            await context.sync();

            if (shape.type === PowerPoint.ShapeType.textBox) {
              const textFrame = shape.textFrame;
              textFrame.load("textRange");
              await context.sync();

              if (textFrame.textRange) {
                textFrame.textRange.load(["text", "font"]);
                await context.sync();

                const textData: any = {
                  shapeName: shape.name,
                  text: textFrame.textRange.text,
                  position: {
                    left: shape.left,
                    top: shape.top,
                    width: shape.width,
                    height: shape.height,
                  },
                };

                if (includeFormatting) {
                  textData.formatting = {
                    fontName: textFrame.textRange.font.name,
                    fontSize: textFrame.textRange.font.size,
                    bold: textFrame.textRange.font.bold,
                    italic: textFrame.textRange.font.italic,
                    color: textFrame.textRange.font.color,
                  };
                }

                slideData.textContent.push(textData);
              }
            }
          }

          exportedData.push(slideData);
        }

        return {
          totalSlides: slides.items.length,
          exportedData: exportedData,
        };
      });
    },
  });

  // === SELECTION TOOLS ===

  client.registerTool("get_selected_text", {
    description: "Get currently selected text in PowerPoint",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return new Promise((resolve, reject) => {
        Office.context.document.getSelectedDataAsync(
          Office.CoercionType.Text,
          (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve({
                selectedText: result.value || "",
                hasSelection: !!result.value,
              });
            } else {
              reject(new Error("Failed to get selected text"));
            }
          }
        );
      });
    },
  });

  console.log("[PowerPointClient] ✅ All tools registered successfully");
}

// Export pour réutilisation
export { registerPowerPointTools };